import { BUILDINGS, techById } from '../core/defs';
import type { BuildingKind, WorldEvent } from '../core/types';
import type { World } from '../core/world';

export type Tone = 'info' | 'good' | 'bad' | 'warn' | 'act';

const CAUSE: Record<string, string> = {
  glitch: 'A glitch got in',
  'flawed-release': 'A hallucinated draft reached users',
  'dependency-break': 'A hidden dependency broke',
  'legacy-collapse': 'The legacy system collapsed',
  'corrupt-release': 'Corrupted work reached users',
};

const SRC: Record<string, string> = { voice: ' (voice)', api: ' (script)', drone: ' (drone)', command: '' };

function label(w: World, id: unknown): string {
  return w.buildings.get(Number(id))?.label ?? 'a machine';
}

/** Plain-language line for the event log. Returns null for events too small to show. */
export function describe(e: WorldEvent, w: World): { text: string; tone: Tone } | null {
  const src = SRC[String(e.src ?? '')] ?? '';
  switch (e.type) {
    case 'place':
      if (e.kind === 'belt' || e.kind === 'firewall') return null;
      return { text: `${e.ghost ? 'Planned' : 'Built'} ${label(w, e.id)}${src}`, tone: 'info' };
    case 'droneBuild':
      return e.kind === 'belt' ? null : { text: `Drones built ${label(w, e.id)}`, tone: 'info' };
    case 'remove':
      return e.kind === 'belt' ? null : { text: `Removed ${BUILDINGS[e.kind as BuildingKind]?.name ?? 'something'}${src}`, tone: 'info' };
    case 'research':
      return { text: `Researched ${techById(String(e.tech))?.name ?? e.tech}${src}`, tone: 'good' };
    case 'secret':
      return { text: `Found ${String(e.name).toLowerCase()}`, tone: 'good' };
    case 'claim':
      return { text: `Claimed ${e.amount} in forgotten budget${src}`, tone: 'good' };
    case 'terminal':
      return { text: 'Logged in to the operator terminal. The API is yours.', tone: 'good' };
    case 'ask':
      return { text: `Asked the review board: ${String(e.label).toLowerCase()}${src}`, tone: 'warn' };
    case 'approval':
      return e.approved
        ? { text: `Approved after ${Math.round(Number(e.waited))}s: ${String(e.label).toLowerCase()}`, tone: 'good' }
        : { text: `Denied after ${Math.round(Number(e.waited))}s: ${String(e.label).toLowerCase()}`, tone: 'bad' };
    case 'act':
      if (e.backfired) return { text: `Went ahead without asking${src}. It broke things.`, tone: 'bad' };
      if (e.risky && e.protected) return { text: `Went ahead without asking${src}. The checkpoint caught what broke.`, tone: 'act' };
      return { text: `Went ahead without asking${src}. Nothing broke.`, tone: 'act' };
    case 'monolith':
      return { text: 'The monolith is open. Rich context underneath.', tone: 'good' };
    case 'incident': {
      const o = String(e.origin ?? '');
      if (e.cause === 'flawed-release') return null;
      if (e.cause === 'glitch') return { text: `A glitch from ${o || 'a nest'} got in`, tone: 'bad' };
      if (e.cause === 'dependency-break') return { text: `A hidden dependency in ${o} broke`, tone: 'bad' };
      if (e.cause === 'legacy-collapse') return { text: 'The legacy monolith collapsed on its way open', tone: 'bad' };
      if (e.cause === 'lost-update') return { text: `${o[0].toUpperCase()}${o.slice(1)}: a newer change vanished`, tone: 'bad' };
      if (e.cause === 'outage') return { text: 'A rack went down', tone: 'bad' };
      return { text: `${CAUSE[String(e.cause)] ?? 'Incident'}${o ? `: ${o}` : ''}`, tone: 'bad' };
    }
    case 'corrupt':
      return { text: `${label(w, e.id)} is corrupted`, tone: 'bad' };
    case 'corruptRelease':
      return { text: 'Corrupted work reached users', tone: 'bad' };
    case 'trip':
      return { text: `${label(w, e.id)} tripped and stopped corrupted work`, tone: 'good' };
    case 'rollback':
      return { text: `Rolled back ${label(w, e.id)}: ${e.restored} machine${e.restored === 1 ? '' : 's'} restored${src}`, tone: 'good' };
    case 'patch':
      return { text: `Hotfixed ${label(w, e.id)}${src}`, tone: 'info' };
    case 'dronePatch':
      return { text: `Drones patched ${label(w, e.id)}`, tone: 'info' };
    case 'nestDestroyed':
      return { text: 'Cleared a glitch nest', tone: 'good' };
    case 'firewallBroken':
      return { text: 'A firewall fell', tone: 'warn' };
    case 'undo':
      return { text: `Undid the last change${src}`, tone: 'info' };
    case 'note':
      return { text: String(e.text), tone: 'info' };
    case 'perturbation':
      return e.text ? { text: String(e.text), tone: 'warn' } : null;
    case 'recovered':
      return { text: `Back to pace ${Math.round(Number(e.after))}s after the change`, tone: 'good' };
    case 'planTest':
      return { text: `Tested the plan: ${e.rate} a minute, trust ${e.trust}${e.overBudget ? ', over budget' : ''}`, tone: 'info' };
    case 'planResult':
      return { text: `Plan simulated: ${e.rate} a minute against ${e.target}`, tone: Number(e.rate) >= Number(e.target) ? 'good' : 'bad' };
    case 'won':
      return { text: 'Goal reached', tone: 'good' };
    case 'lost':
      return { text: 'Run over', tone: 'bad' };
    default:
      return null;
  }
}

/** Events worth a toast the moment they happen (the log keeps the rest). */
export function toastWorthy(e: WorldEvent): boolean {
  return ['secret', 'approval', 'act', 'monolith', 'corruptRelease', 'nestDestroyed', 'terminal', 'claim', 'recovered'].includes(e.type) || (e.type === 'incident' && e.cause !== 'flawed-release' && e.cause !== 'lost-update');
}
